import React, { useState } from 'react';
import { 
  DataGrid, 
  GridColDef, 
  GridActionsCellItem 
} from '@mui/x-data-grid';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Stack, 
  TextField, 
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { 
    useActualizarFrigorficoMutation, 
    useAgregarFrigorficoMutation, 
    useBorrarFrigorficoMutation, 
    useGetFriosByPlantaIdQuery 
} from '../../../api/frioApi';
import { useNavigate } from 'react-router-dom';
import { ItemFrigorifico, ItemPlanta } from '../interfaces/avanzado.interface';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { handleMutationError } from '../../../api/handleMutationError';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

type CardProps = {
  propsPlanta: ItemPlanta//string;
}

export const AvanzadoFrigorificoComponent: React.FC<CardProps> = ({propsPlanta}) => {

  const navigate = useNavigate();

  const { data = [], isLoading, isError, error, refetch } = useGetFriosByPlantaIdQuery(propsPlanta.id, {});
  const [addItem, {isLoading: isLoadingAdd, isError: isErrorAdd, error: errorAdd, reset: resetAdd}] = useAgregarFrigorficoMutation();
  const [updateItem, {isLoading: isLoadingUpdate, isError: isErrorUpdate, error: errorUpdate}] = useActualizarFrigorficoMutation();
  const [deleteItem, {isLoading: isLoadingDelete, isError: isErrorDelete, error: errorDelete}] = useBorrarFrigorficoMutation();
  
  const [editItem, setEditItem] = useState<ItemFrigorifico | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [selectedIdDelete, setSelectedIdDelete] = useState<string | null>(null);

  
  const handleAddClick = () => {
    setEditItem(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (item: ItemFrigorifico) => {
    setEditItem(item);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedIdDelete(id);
    setOpenDialogDelete(true);
  };

  const confirmDelete = async () => {
    try {
      if (selectedIdDelete !== null) {
        await deleteItem(selectedIdDelete).unwrap();
        setOpenDialogDelete(false);
        setSelectedIdDelete(null);
        refetch();
      }
    } catch (err) {
      // Handle error, display message
      console.error("Error al elminar el frigorifico", err);
    }   
  };

  const cancelDelete = () => {
    setOpenDialogDelete(false);
    setSelectedIdDelete(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditItem(null);
    resetAdd();
  };

  const handleSaveClick = async (plantaId: string) => {
    try{
      if (editItem) {
        if (editItem.id) {
          await updateItem(
            {
              id: editItem.id, 
              name: editItem.name, 
              name_front: editItem.name_front, 
              ubicacion: editItem.ubicacion,
              orden: editItem.orden, 
              planta: plantaId
            }
          ).unwrap();
        } else {
          await addItem(
            {
              name: editItem.name, 
              name_front: editItem.name_front, 
              ubicacion: editItem.ubicacion,
              orden: editItem.orden, 
              planta: plantaId
            }

          ).unwrap();
        }
        refetch();
        handleDialogClose();
      }
    } catch (err) {
      // Handle error, display message
      console.error("Error al guardar frigorifico", err);
    }
  };

  const handleViewPlantasClick = () => {
    navigate("/avanzado");
  };

  const handleViewCamerasClick = (planta: ItemPlanta,  frio: ItemFrigorifico) => {
    navigate("/avanzado/camaras",{state: {planta, frio}});
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Nombre',flex: 1, minWidth: 150, editable: false },
    { field: 'name_front', headerName: 'Nombre Front', flex: 1, minWidth: 150, editable: false },
        {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      flex: 0.5,
      minWidth: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          color="primary"
          label="Edit"
          onClick={() => handleEditClick(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          color="error"
          label="Delete"
          onClick={() => handleDeleteClick(params.row.id as string)}
        />,
      ],
    },
    { field: 'ubicacion', headerName: 'Ubicacion', flex: 0.5, minWidth: 80, editable: false },
    { field: 'orden', headerName: 'Orden Aparicion',flex: 0.5, minWidth: 80, editable: false },
    {
      field: 'planta',
      headerName: 'Planta',
      flex: 0.7,
      minWidth: 150,
      // Accediendo al valor anidado dentro de `item`
      valueGetter: (params, row) => row.planta?.name ?? 'N/A',
    },
    { field: 'id', headerName: 'ID',flex: 0.3, minWidth: 60 },
    {
      field: 'ir',
      headerName: 'Ver',
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleViewCamerasClick(propsPlanta, params.row)}
          startIcon={<NavigateNextIcon />}  // Ícono que aparece al principio del botón
        >
          CAMARAS
        </Button>
      ),
    }
  ];

  return (
    <div>
    {isLoading ? (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    ) :
      <Box sx={{ height: '100%', width: '100%', padding: 2 }}>
        {/* Título del DataGrid */}
        <Typography variant="h5" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
          Lista de frigorificos registrados
        </Typography>
        <Stack sx={{my: 2}} spacing={2} direction="row">
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleViewPlantasClick()}
            startIcon={<NavigateBeforeIcon />} 
          >
            PLANTAS
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            AGREGAR FRIGORIFICOS
          </Button>
        </Stack>
        <DataGrid
          rows={data!.data}
          columns={columns}
          editMode="row"
          onRowEditStop={() => {}}
        />
        
        <Dialog open={isDialogOpen} onClose={handleDialogClose}>
          <DialogTitle>{editItem ? 'Editar Frigorifico' : 'Agregar Frigorifico'}</DialogTitle>
          <DialogContent>
            {(isLoadingAdd || isLoadingUpdate) && <CircularProgress />}
            {isErrorAdd && <p>Error al guardar: {errorAdd? handleMutationError(errorAdd) : 'Falla no reconocida'}</p>}
            {isErrorUpdate && <p>Error al actualizar: {errorUpdate? handleMutationError(errorUpdate) : 'Falla no reconocida'}</p>}            
            <TextField
              autoFocus
              margin="dense"
              label="Nombre"
              fullWidth
              variant="standard"
              value={editItem?.name || ''}
              onChange={(e) => setEditItem({ ...editItem, name: e.target.value } as ItemFrigorifico)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Nombre Front"
              fullWidth
              variant="standard"
              value={editItem?.name_front || ''}
              onChange={(e) => setEditItem({ ...editItem, name_front: e.target.value } as ItemFrigorifico)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Ubicacion"
              fullWidth
              variant="standard"
              value={editItem?.ubicacion || ''}
              onChange={(e) => setEditItem({ ...editItem, ubicacion: e.target.value } as ItemFrigorifico)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Orden Aparición"
              fullWidth
              variant="standard"
              value={editItem?.orden || ''}
              onChange={(e) => setEditItem({ ...editItem, orden: e.target.value } as ItemFrigorifico)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              Cancelar
            </Button>
            <Button onClick={() => handleSaveClick(propsPlanta.id as string)} color="primary">
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDialogDelete} onClose={cancelDelete}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Está seguro de que desea eliminar el friogrifico?
            </Typography>
          </DialogContent>
          <DialogActions>
            {isLoadingDelete && <CircularProgress />}
            {isErrorDelete && <p>Error al borrar: {errorDelete? handleMutationError(errorDelete) : 'Falla no reconocida'}</p>}            
            <Button onClick={cancelDelete} color="primary">
              Cancelar
            </Button>
            <Button onClick={confirmDelete} color="error">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
    }
      {isError && <p>Falla Conexion con el servidor: {error? handleMutationError(error) : 'Falla no reconocida'}</p>}
    </div>
  );
};